import moment, { Moment } from 'moment'
import { safeNumber } from '../../../../helper/common'
import {
  IContractDetails,
  ManualContractCreationRequestData
} from 'features/ContractDetails/types'
import { IContractDetailsForm } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/types'

export const convertDetailsToForm = (
  details: IContractDetails
): IContractDetailsForm => ({
  start_date: details.start_date ? moment(details.start_date) : null,
  finish_date: details.finish_date ? moment(details.finish_date) : null,
  admin_fee: details.admin_fee ?? null,
  payment_term_days: details.payment_term_days || null,
  payment_frequency: details.payment_frequency || null,
  vendor_fee_type: details.vendor_fee_type,
  contract_category: details.contract_category_id || null,
  organization_contract_category: details.contract_category_id || null,
  custom_number: details.custom_number || null,
  shipping_method: details.shipping_method || null
})

export const convertFormToDetails = (
  form: IContractDetailsForm
): Partial<IContractDetails> => ({
  vendor_fee_type: form.vendor_fee_type,
  start_date: form.start_date
    ? moment(form.start_date).format('YYYY-MM-DD')
    : null,
  primary_finish_date: form.finish_date
    ? moment(form.finish_date).format('YYYY-MM-DD')
    : null,
  payment_term_days: form.payment_term_days || 0,
  payment_frequency: form.payment_frequency || null,
  admin_fee: form.admin_fee ?? '0',
  custom_number: form.custom_number || '',
  shipping_method: form.shipping_method || null
})

export const convertFormToManualCreation = (
  form: IContractDetailsForm
): Partial<ManualContractCreationRequestData> => {
  const requestData: Partial<ManualContractCreationRequestData> = {}

  if (form.start_date) {
    requestData.start_date = moment(form.start_date).format('YYYY-MM-DD')
  }
  if (form.finish_date) {
    requestData.primary_finish_date = moment(form.finish_date).format(
      'YYYY-MM-DD'
    )
  }
  if (form.admin_fee) {
    requestData.admin_fee = Number(form.admin_fee)
  }
  if (form.vendor_fee_type) {
    requestData.vendor_fee_type = form.vendor_fee_type
  }
  if (form.payment_term_days) {
    requestData.payment_term_days = Number(form.payment_term_days)
  }
  if (form.payment_frequency) {
    requestData.payment_frequency = form.payment_frequency
  }
  if (form.organization_contract_category) {
    requestData.organization_contract_category =
      form.organization_contract_category
  }
  if (form.vendor_contract_steward) {
    requestData.vendor_contract_steward = form.vendor_contract_steward
  }
  if (form.vendor) {
    requestData.vendor = form.vendor
  }
  if (form.shipping_method) {
    requestData.shipping_method = form.shipping_method
  }
  if (form.custom_number) {
    requestData.custom_number = form.custom_number
  }
  return requestData
}

export const twoDecimalParser = (value: string | undefined) =>
  value ? parseFloat(value).toFixed(2) : ''

export const numericValueParser = (value: string | undefined) =>
  value ? parseFloat(value).toFixed(0) : ''

export const currencyParser = (value: string | undefined) => {
  const matches = value?.match(/.+[.]\d{0,2}/)
  const matchedValue = matches ? matches[0] : value

  return matchedValue ? matchedValue.replace(/\$\s?|(,*)/g, '') : ''
}

export const currencyParserInteger = (value: string | undefined) => {
  if (!value) {
    return ''
  }
  const integerNumber = value.replace(/[^\d.]/g, '')
  const parsedValue = parseFloat(integerNumber)
  return isNaN(parsedValue) ? '' : Math.floor(parsedValue).toString()
}

export const percentageParser = (value: string | undefined) => {
  if (!value) {
    return ''
  }
  const integerNumber = value.replace(/[^\d.]/g, '')
  const parsedValue = parseFloat(integerNumber)
  const isInteger = Number.isInteger(parsedValue)
  return isNaN(parsedValue)
    ? ''
    : isInteger
    ? parsedValue.toString()
    : parsedValue.toFixed(2).toString()
}

export const currencyFormatter = (value: string | number | undefined) =>
  String(value).replace(/\B(?=(\d{3})+(?!\d).?)/g, ',')

export const currencyFormatterTwoDecimals = (
  value: string | number | undefined
) => {
  if (value && String(value).indexOf('.') > -1) {
    const currencyParts = String(value).split('.')
    return `${currencyFormatter(currencyParts[0])}.${currencyParts[1].slice(
      0,
      2
    )}`
  } else return currencyFormatter(value)
}

export const getCurrencyValue = (value: number) => {
  const formatter = Intl.NumberFormat('en', { notation: 'compact' })
  return formatter.format(value)
}

export const disabledDateSameOrBefore =
  (prevDate: Moment | null) => (current: Moment | null) => {
    if (!current) return false

    const currentDate = current.startOf('day')
    const today = moment().startOf('day')

    if (!prevDate) {
      return !currentDate.isAfter(today)
    }

    const prev = prevDate.startOf('day')
    return !currentDate.isAfter(prev)
  }

export const disabledDateFinishDt =
  (finishDate: Moment | null) => (current: Moment | null) => {
    if (!current) return false

    const currentDate = current.startOf('day')

    if (!finishDate) {
      return false
    }

    return !currentDate.isBefore(finishDate.startOf('day'))
  }

export const sumPercentage = (firstValue: string, secondValue: string) =>
  (
    (Number(firstValue) * 100) /
    (Number(firstValue) + Number(secondValue))
  ).toFixed(2)

export const sumPercentageTotal = (firstValue: string, total: number) =>
  ((Number(firstValue) * 100) / Number(total)).toFixed(2)

export const totalSpendCounting = (value: number) => ({
  total: value,
  third: (value * 3) / 4,
  second: (value * 2) / 4,
  first: (value * 1) / 4,
  zeroth: 0
})

export const getHeight = (num: string, max: number) => {
  return (107 * +num || 0) / safeNumber(ceilIntNumber(max))
}

export const convertToFixed = (value: string | number) =>
  Number(value).toFixed(2)

export const ceilIntNumber = (value: number) => Math.ceil(value / 1000) * 1000
